import {
  Box,
  Button,
  DialogActions,
  IconButton,
  MenuItem,
  Select,
  Switch,
} from "@material-ui/core";
import Alert from "@material-ui/lab/Alert";
import React, { useEffect, useState, useCallback, memo } from "react";
import EditOutlinedIcon from "@material-ui/icons/EditOutlined";
import { useAsyncAction } from "modus/components/form/async-actions/AsyncAction";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import Typography from "legacy-components/typography/Typography";
import ApplicationService from "pages/home/applications/components/v2.0/ApplicationService";
import { formatPublisherHttpError } from "../services/ApiService";
import { useDialogProps, Dialog } from "legacy-components/dialog/Dialog";
import { InputContainer } from "modus/components/form/input-container/InputContainer";
import { useAuthorization } from "../../authorizer/components/AuthorizationProvider";
import { TimeUnit } from "./ThrottlingDetails";

const supportRegionalDeployments =
  process.env.REACT_APP_SUPPORT_REGIONAL_DEPLOYMENTS === "true";

interface SpikeArrestFormProps {
  deploymentId: string;
  availableTimeUnit: readonly TimeUnit[];
  spikeArrestEnabled: boolean ;
  spikeArrestConfig: {
    enabled: boolean;
    limit: number;
    unit: string;
  } | null;
  SubscriptionQuotaConfig: {
    enabled: boolean;
    limit: number;
    interval: number;
    unit: string;
  } | null;
  deploymentStatus: string | null;
  onSuccess: () => void;
  serviceId: string;
}


const SpikeArrestForm: React.FC<SpikeArrestFormProps> = memo(({
  // onCancel,
  deploymentId,
  availableTimeUnit,
  spikeArrestEnabled,
  spikeArrestConfig,
  deploymentStatus,
  SubscriptionQuotaConfig,
  onSuccess,
    serviceId
}) => {
  const [spikeArrestTimeUnit, setspikeArrestTimeUnit] = useState<string>(spikeArrestConfig?.unit ?? availableTimeUnit[0].value);
  const [spikeArrestLimit, setSpikeArrestLimit] = useState<string>(spikeArrestConfig?.limit?.toString() ?? "");
  const [isSpikeArrestActive, setIsSpikeArrestActive] = useState<boolean>(spikeArrestEnabled=== undefined ? false : spikeArrestEnabled);
  const [hasFormError, setHasFormError] = useState<boolean>(false);

  const { open, toggle } = useDialogProps();

  const resetForm = useCallback(() => {
    setSpikeArrestLimit(spikeArrestConfig?.limit?.toString() ?? "");
    setspikeArrestTimeUnit(spikeArrestConfig?.unit ?? "");
  }, [spikeArrestConfig]);

  const handleClose = useCallback(() => {
    resetForm();
    setHasFormError(false);
    setspikeArrestTimeUnit(spikeArrestConfig?.unit ?? availableTimeUnit[0].value);
    toggle();
  }, [resetForm, toggle, setHasFormError, setspikeArrestTimeUnit, spikeArrestConfig, availableTimeUnit]);


  const { state, subscribe } = useAsyncAction(() => {
    const body = buildRequestBody(
      spikeArrestLimit,
      spikeArrestTimeUnit,
      SubscriptionQuotaConfig
    );
    return supportRegionalDeployments
      ? ApplicationService.createApiThrottlingV2(deploymentId, body)
      : ApplicationService.createApiThrottling(deploymentId, body);
  });


  function buildRequestBody(
    spikeArrestLimit: any,
    spikeArrestTimeUnit: any,
    SubscriptionQuotaConfig: any
  ) {
    return {
      quotaEnabled: SubscriptionQuotaConfig?.enabled ?? false,
      quotaConfig: [
        {
            path: "/*",
            verb: "*",
            limit: SubscriptionQuotaConfig?.limit ?? 100,
            interval: SubscriptionQuotaConfig?.interval ?? 300,
            unit: SubscriptionQuotaConfig?.unit ?? "MINUTE",
        }
      ],
      spikeArrestEnabled: isSpikeArrestActive,
      spikeConfig:
        {
          limit: spikeArrestLimit === '' ? (spikeArrestConfig?.limit ?? 0) : Number(spikeArrestLimit),
          unit: spikeArrestTimeUnit ?? spikeArrestConfig?.unit ?? '',
        }
    };
  }

    useEffect(() => {
      if (!state?.loaded || state?.error || !onSuccess) {
        return;
      }
      onSuccess();
    }, [onSuccess, state?.loaded, state?.error]);

  const handleSubmit = useCallback(() => {
    if(!subscribe || !toggle){
      return;
    }
    subscribe();
    if(open){
      resetForm();
      setspikeArrestTimeUnit(spikeArrestConfig?.unit ?? availableTimeUnit[0].value);
      toggle();
    }
  }, [subscribe, toggle, open, resetForm, setspikeArrestTimeUnit, availableTimeUnit, spikeArrestConfig]);




  const onSpikeArrestToggle = useCallback(()=>{
    if(!toggle || !setIsSpikeArrestActive|| !spikeArrestConfig){
      return ;
    }
    else{
      setIsSpikeArrestActive(!isSpikeArrestActive); 
    }
  }, [setIsSpikeArrestActive, isSpikeArrestActive, spikeArrestConfig, toggle]);


  useEffect(()=>{
    if(!subscribe || !toggle || spikeArrestEnabled===undefined || !setIsSpikeArrestActive || spikeArrestConfig===undefined|| SubscriptionQuotaConfig===undefined){return}

   if(SubscriptionQuotaConfig === null){
      setIsSpikeArrestActive(open);
    }

    else if(spikeArrestEnabled === isSpikeArrestActive){
      return
    }

    else if(isSpikeArrestActive === false){
      subscribe()
    }

    else if (isSpikeArrestActive === true && spikeArrestConfig !== null){
      subscribe()

    }
  }, [isSpikeArrestActive, subscribe, toggle, spikeArrestConfig, spikeArrestEnabled, setIsSpikeArrestActive, SubscriptionQuotaConfig, open])

  const maxlimit = () => {
    if (spikeArrestTimeUnit === "MINUTE") {
      return 180000;
    }
    else if (spikeArrestTimeUnit === "SECOND") {
      return 3000;
    }
    return 180000;
  }
      const { isActionAllowed } = useAuthorization("SERVICE" , serviceId);
      const canUpdate = isActionAllowed("update");
  const isSwitchDisabled = deploymentStatus === "DEPLOY_INITIATED" || spikeArrestConfig===undefined || spikeArrestConfig===null || (spikeArrestConfig?.limit === 0 && spikeArrestConfig?.unit === "SECOND") || state?.loading || !canUpdate;
  const isUpdateButtonDisabled = deploymentStatus === "DEPLOY_INITIATED" || spikeArrestLimit === "" || spikeArrestTimeUnit === "" || state?.loading || hasFormError;

  const throttlingErrorMessage =
    state?.error != null
      ? formatPublisherHttpError(
          state.error,
          "Could not update throttling. Please try again."
        )
      : null;

  return (
    <div>
      {throttlingErrorMessage && (
        <Alert severity="error" style={{ marginBottom: 8 }}>
          {throttlingErrorMessage}
        </Alert>
      )}
      <Switch
        size="small"
        color="primary"
        onChange={onSpikeArrestToggle}
        checked={isSpikeArrestActive}
        disabled={isSwitchDisabled}
      />
      <IconButton onClick={toggle} disabled={deploymentStatus === "DEPLOY_INITIATED" || !canUpdate}>
        <EditOutlinedIcon />
      </IconButton>
      <Dialog open={open} title={"Spike Arrest"} onClose={handleClose}>
        <Box>
          <Typography type="body">
            API Transaction limit
            <span className="required">*</span>
          </Typography>
          <InputContainer
            variant="standard"
            autoFocus
            margin="dense"
            id="apiHitLimit"
            placeholder="Number of API hits allowed"
            fullWidth
            required={true}
            rows={1}
            isOnTypeValidationEnabled={true}
            value={spikeArrestLimit}
            onChange={(event) => {
                setSpikeArrestLimit(event.target.value);
            }}
            setError={setHasFormError}
            validate={{
              required: true,
              pattern: "^\\d+$",
              max: maxlimit(),
              min: 1
            }}
          />
          <Box mt={3}></Box>
          <Typography type="body">
            Time
            <span className="required">*</span>
          </Typography>
          <Select
            id="spikeArrest"
            IconComponent={ExpandMoreIcon}
            variant="outlined"
            className="header-selector resource-version-select"
            placeholder="Spike Arrest Time unit"
            style={{ minWidth: "100%" }}
            onChange={(event) => {
              const value = event.target.value as string;
              setspikeArrestTimeUnit(value);
              if (value) {
                setHasFormError(false);
              }
            }}
            value={spikeArrestTimeUnit}
          >
            {availableTimeUnit?.map((unit) => {
              return (
                <MenuItem value={unit.value} key={unit.value}>
                  {unit.key}
                </MenuItem>
              );
            })}
          </Select>
        </Box>
        <DialogActions style={{marginTop: "20px"}}>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={isUpdateButtonDisabled}
          >
            Update
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
);
export default SpikeArrestForm;
